import React, {useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import LineChart from "../LineChart/LineChart";
import chartIcon from '../../../line-chart.png';
import Collapse from '@mui/material/Collapse';
import {fetchData, valueFormatted, yearList} from "../../../utils/helpsers";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {DataGridPro, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton} from "@mui/x-data-grid-pro";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import {ComparisonStyles} from './ComparisonCard.styles';
import PropTypes from "prop-types";
import ErrorLog from "../../molecules/ErrorLog/ErrorLog";
import TooltipCustom from "../../molecules/CustomTooltip/CustomTooltip";
import {red} from '@mui/material/colors';
import NullDisplay, { NULL_DISPLAY_TEXT } from '../../atoms/NullDisplay/NullDisplay';

/**
 * Renders a Card component with data related with the variable set static o dynamically.
 *
 * @param {object} defaultDomainVar: Default key/value variable data.
 * @param {string} year: The year.
 * @param {string} title: The string to fill the card  title.
 * @param {object} comparisonData: Object with data to be map and displayed on the card.
 * @param {object} referenceDistrict: Data from the reference district selected.
 * @param {object, string} irns: object array with district group irn.
 * @param {string, object} groupName: Name of the districts group selected.
 * @param {string, object} groupType: Name of the group type selected.
 * @param {object, array} domainOptions: Data of every options to fill select field. Required keys:
 * @param {function} storedCardInfo: Returns the variable data set in the card on load or select change. Used to save data card info inside comparisonCardsInfo.
 *                 - value
 *                 - label
 * @param {number} id: The element ID to print.
 * @returns {JSX.Element}
 */
const ComparisonCard = ({ defaultDomainVar = null, year, title, comparisonData, referenceDistrict, irns, groupName, groupType, domainOptions = null, storedCardInfo, id, groupDefinition }) => {
  const [varData, setVarData] = useState(defaultDomainVar);
  const [cardData, setCardData] = useState(comparisonData);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showRankingList, setShowRankingList] = useState(false);
  // Data to feed line.
  const [lineData, setLineData] = useState();

  // For error fetch hook
  const [error, setError] = useState(null);

  // User environment.
  const env = process.env.NODE_ENV;

  useEffect(() => {
    storedCardInfo((varData || domainOptions[0]), id);
  }, [varData, domainOptions, storedCardInfo, id])

  useEffect(() => {
    const fetchController = new AbortController();

    if (varData) {
      let splitLD = varData.value.split('__');

      if (splitLD.length > 1) {
        let variable = splitLD[0];
        let planNum = splitLD[1];
        let planType = splitLD[2];

        if (varData.value && referenceDistrict.value && irns && year) {
          fetchData({
            endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_dynamic_card_health?_var=${variable}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year}&_plannum=${planNum}&_plantype=${planType}`,
            controller: fetchController
          }).then(response => {
            let data = response.data[0];
            if (typeof data === 'object') {
              setCardData(data)
            } else {
              setError({ type: 'Data Error', message: `Invalid type data type ${data}, expected an object` })
            }
          }).catch(error => setError({ type: 'Fetch Error', ...error }))
        }
        if (referenceDistrict.value && irns && groupName) {
          fetchData({
            endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_historic_variable_health?_irn=${referenceDistrict.value}&_irns=${irns}&_group_name=${encodeURIComponent(groupName)}&_variable=${variable}&_plannum=${planNum}&_plantype=${planType}`,
            controller: fetchController
          }).then(response => {
            let data = response.data;
            if (Array.isArray(data)) {
              let chartData = data.map(item => {
                if (referenceDistrict.value === item.irn) return { ...item, color: '#F5B335', zIndex: 2}
                if (item.name === "Statewide") return {...item, color: red[800]}
                return { ...item, color: '#773DBD', zIndex: 1 }
              })

              chartData.sort((a, b) => {
                if (a.name === 'Statewide') return 1;
                if (b.name === 'Statewide') return -1;
                if (a.irn !== null && b.irn === null) return -1;
                // Maintain order otherwise
                return 0;
              });

              if (data[0] && data[1]) {
                // Order years and data
                let orderVar = {};
                orderVar[0] = {};
                orderVar[1] = {};

                chartData[0].data?.forEach((element, index) => {
                  orderVar[0][chartData[0].years[index]] = element;
                });

                chartData[1].data?.forEach((element, index) => {
                  orderVar[1][chartData[1].years[index]] = element;
                });

                let a = Object.keys(orderVar[0]);
                let b = Object.keys(orderVar[1]);

                let unique_years = Array.from(new Set([...a, ...b].sort())).sort();
                let setA = [];
                let setB = [];

                unique_years.forEach(element => {
                  setA.push(orderVar[0][element] !== undefined ? orderVar[0][element] : null);
                  setB.push(orderVar[1][element] !== undefined ? orderVar[1][element] : null);
                });

                chartData[0].data = setA;
                chartData[0].years = unique_years;
                chartData[1].data = setB;
                chartData[1].years = unique_years;

                setLineData(chartData);
              }
            }
          }).catch(error => setError({ type: 'Fetch Error', ...error }))
        }
      } else {
        if (varData.value && referenceDistrict.value && irns && year) {
          fetchData({
            endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_dynamic_card?_var=${varData.value}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year}`,
            controller: fetchController
          }).then(response => {
            let data = response.data[0];
            if(typeof data === 'object') {
              // Customization for MCD logic in Teacher Salary
              if(data.domain === 'Teacher Salary' && year==="MCD") {
                // Removing year from kpi label.
                data.kpi = data.kpi?.replace(/\s*\(\d{4}\)$/, "");
                // Filter nulls and mark non-2026 districts as "No Data"
                // We cannot distinguish between not-harvested and not-in-SERB at this level
                data.list = data?.list?.filter(el => el.value !== null).map(item => {
                  if(item.irn === referenceDistrict.value && item.year !== 2026) {
                    data.value = null
                    data.null_reason = "NR"
                    data.rank_in_group = null
                  }
                  if(data.group_average === null) {
                    data.group_average = null
                  }
                  return ({
                    ...item,
                    value: (item?.year !== 2026) ? null : item.value,
                    null_reason: (item?.year !== 2026) ? "NR" : undefined,
                  })
                })

                // Separate numeric (2026) and null (no data) values
                let numericValues = [];
                let nullValues = [];
                for (const el of data.list) {
                  (el.value === null ? nullValues : numericValues).push(el);
                }

                // Re-rank ALL Teacher Salary/MCD variables among 2026 districts only
                // using standard ranking (RANK, not DENSE_RANK).
                // This is needed because:
                // 1. The API ranks ALL districts (2025+2026) together
                // 2. We hide 2025 districts as "No Data", creating gaps
                // 3. Districts with identical values should have the same rank
                // 4. Standard ranking: if 4 items tie at rank 1, next rank is 5 (not 2)
                //
                // Preserve the API's sort order (already sorted by higher_number preference)
                // by collecting unique values in the order they appear, with counts.
                const valueRanks = new Map(); // value -> standard rank
                let position = 1;
                numericValues.forEach(item => {
                  if (!valueRanks.has(item.value)) {
                    valueRanks.set(item.value, position);
                  }
                  position++; // Always increment position for standard ranking
                });

                data.list = [...numericValues, ...nullValues].map(item => {
                  const rank = item.value !== null ? valueRanks.get(item.value) : null;
                  if (item.irn === referenceDistrict.value && item.year === 2026) {
                    data.rank_in_group = rank;
                  }
                  return {
                    ...item,
                    rown: rank,
                  };
                }).sort((a, b) => a.rown - b.rown);
              }

              setCardData(data)
            } else {
              setError({ type: 'Data Error', message: `Invalid type data type ${data}, expected an object` })
            }
          }).catch(error => setError({ type: 'Fetch Error', ...error }))
        }
        if (referenceDistrict.value && irns && groupName && varData.value) {
          fetchData({
            endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_historic_variable?_irn=${referenceDistrict.value}&_irns=${irns}&_group_name=${encodeURIComponent(groupName)}&_variable=${varData.value}`,
            controller: fetchController
          }).then(response => {
            let data = response.data;
            if (Array.isArray(data)) {
              let chartData = data.map(item => {
                if (referenceDistrict.value === item.irn) return { ...item, color: '#F5B335', zIndex: 2}
                if (item.name === "Statewide") return {...item, color: red[800]}
                return { ...item, color: '#773DBD', zIndex: 1 }
              })

              chartData.sort((a, b) => {
                if (a.name === 'Statewide') return 1;
                if (b.name === 'Statewide') return -1;
                if (a.irn !== null && b.irn === null) return -1;
                // Maintain order otherwise
                return 0;
              });

              if (data[0] && data[1]) {
                // Order years and data
                let orderVar = {};
                orderVar[0] = {};
                orderVar[1] = {};

                chartData[0].data?.forEach((element, index) => {
                  orderVar[0][chartData[0].years[index]] = element;
                });

                chartData[1].data?.forEach((element, index) => {
                  orderVar[1][chartData[1].years[index]] = element;
                });

                let a = Object.keys(orderVar[0]);
                let b = Object.keys(orderVar[1]);

                let unique_years = Array.from(new Set([...a, ...b].sort())).sort();
                let setA = [];
                let setB = [];

                unique_years.forEach(element => {
                  setA.push(orderVar[0][element] !== undefined ? orderVar[0][element] : null);
                  setB.push(orderVar[1][element] !== undefined ? orderVar[1][element] : null);
                });

                chartData[0].data = setA;
                chartData[0].years = unique_years;
                chartData[1].data = setB;
                chartData[1].years = unique_years;

                setLineData(chartData);
              }
            } else {
              setError({ type: 'Data Error', message: `Invalid type data type ${data}, expected an array` })
            }
          }).catch(error => setError({ type: 'Fetch Error', ...error }))
        }
      }
    } else if (domainOptions && !varData) {
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_dynamic_card?_var=${domainOptions[0].value}&_irn=${referenceDistrict.value}&_irns=${irns}&_year=${year}`,
        controller: fetchController
      }).then(response => {
        setCardData(response.data[0]);
      }).catch(error => setError({ type: 'Fetch Error', ...error }));
      fetchData({
        endpoint: `${process.env.REACT_APP_API_URL}/rpc/hvd_historic_variable?_irn=${referenceDistrict.value}&_irns=${irns}&_group_name=${encodeURIComponent(groupName)}&_variable=${domainOptions[0].value}`,
        controller: fetchController
      }).then(response => {
        setLineData(response.data);
      }).catch(error => setError({ type: 'Fetch Error', ...error }));

      storedCardInfo(domainOptions[0], id);
    }

    // Cleaning component.
    return function cleanup() {
      fetchController?.abort("Unmounted Comparison Card component");
    }
  }, [year, groupName, varData, irns, referenceDistrict.value, domainOptions, storedCardInfo, id]);

  let average = () => {
    let value = Math.sign(cardData.average_position);
    let flag = '';
    switch (cardData.higher_number) {
      // Green: >0 values are positive
      // Example: Total Expenditure per pupil, if a district spends more money per student the amount is considered a "positive" value.
      case 'Green':
        if (value === -1) {
          flag = "negative";
        } else {
          flag = "positive";
        }
        break;
      // Red: negative numbers (<0) are considered a "positive" variable value.
      // Example: The mobility rate, if students changing schools is higher than the group average, it is considered a "negative" value.
      case 'Red':
        if (value === -1) {
          flag = "positive";
        } else {
          flag = "negative";
        }
        break;
      // Default: Neutral values do not require a negative/positive color.
      default:
        flag = '';
        break;
    }

    return flag;
  }

  let sentenceRender = () => {
    // Return empty string if district value is null (e.g., not in SERB).
    if(cardData.value === null) {
      return ""
    }

    let sentence = cardData.sentence;
    if (["Percentage Change in Enrollment Over 10 Years", "Percentage Change in Enrollment Over 5 Years"].some(el => cardData.kpi.includes(el))) {
      let sentenceOptions = sentence.split("#");
      if (cardData.value > 0 && cardData.group_average > 0) {
        sentence = sentenceOptions[1];
        sentence = sentence.slice(2).trim();
      } else if (cardData.value < 0 && cardData.group_average < 0) {
        sentence = sentenceOptions[2];
        sentence = sentence.slice(2).trim();
      } else {
        sentence = sentenceOptions[3];
        sentence = sentence.slice(2).trim();
      }
    }

    let district_short_name = referenceDistrict.label.split(',')[0];

    sentence = sentence.replace("(district_short_name)", district_short_name);
    sentence = sentence.replace("(district_name)", district_short_name);
    sentence = sentence.replace("(district)", district_short_name);
    sentence = sentence.replace("(comparison_group_name)", groupName);
    sentence = sentence.replace("(human name)", year === 'MCD' ? (cardData.kpi).replace(`(${yearList[1].label})`, '') : cardData.kpi);

    if (cardData.average_position !== null) {
      const years_to_vars = ["Years to $50k yield (BA Index)", "Years to $50k yield (MA Index)"];
      const avg_position = years_to_vars.includes(cardData.kpi) ? Math.round(cardData.average_position) : cardData.average_position.toFixed(2)

      if(+avg_position !== 0) {
        sentence = sentence.replace("---", "<span class=\"average-value " + average() + "\">" + Math.abs(avg_position) + "</span>").replaceAll(' %', '%');
      }

      if (avg_position > 0) {
        sentence = sentence.replace("higher/lower", "higher");
        sentence = sentence.replace("faster/slower", "faster");
        sentence = sentence.replace("slower/faster", "slower");
        sentence = sentence.replace("grown/declined", "grown");
        sentence = sentence.replace("more/less", "more");
        sentence = sentence.replace("greater/less", "greater");

        sentence = sentence.replace("more/fewer", "more ");
      }
      else if (avg_position < 0) {
        sentence = sentence.replace("higher/lower", "lower");
        sentence = sentence.replace("faster/slower", "slower");
        sentence = sentence.replace("slower/faster", "faster");
        sentence = sentence.replace("grown/declined", "declined");
        sentence = sentence.replace("more/less", "less");
        sentence = sentence.replace("greater/less", "less");

        sentence = sentence.replace("more/fewer", "fewer ");
      }
    }

    // If average is equal to zero.
    sentence = sentence.replace("higher/lower", "");
    sentence = sentence.replace("faster/slower", "");
    sentence = sentence.replace("slower/faster", "");
    sentence = sentence.replace("grown/declined", "");
    sentence = sentence.replace("more/less", "");
    sentence = sentence.replace("more/fewer", "");

    const patter_to_replace = /\$?---/g;
    sentence = sentence.replace(patter_to_replace, "equal");

    if (groupType === 'District+Picker') {
      sentence = sentence.replace(" group average.", ".");
    }

    // If there is a second value to replace in sentence.
    // Example: Enrollment Percentage Change in Enrollment Over` 10 Years.
    if(sentence.includes("---")) {
      sentence = sentence.replace("---", cardData.group_average === null ? NULL_DISPLAY_TEXT : `<span class="average-value">${cardData.group_average}</span>`).replace(' %', '%');
    }

    // If no sentence (n/a) return empty.
    return sentence === 'n/a' ? '' : sentence;
  }

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
        <GridToolbarExport printOptions={{
          pageStyle: '.MuiDataGrid-toolbarContainer{display:none!important}.MuiDataGrid-root div[data-field="district"],.MuiDataGrid-root div[data-field="value"]{width:300px!important;min-width:300px!important;max-width:300px;}',
        }} />
      </GridToolbarContainer>
    );
  }

  const columns = [
    { field: 'id', headerName: 'ID', hide: true },
    {
      field: 'rown',
      headerName: '#',
      width: 20,
      editable: false,
      valueFormatter: (params) => params.value === null ? '' : params.value,
    },
    {
      field: 'irn',
      headerName: 'IRN',
      width: 65,
      editable: false,
    },
    {
      field: 'district',
      headerName: 'District',
      flex: 1,
      editable: false,
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: .5,
      editable: false,
      renderCell: (params) => params.value === null
        ? <NullDisplay category={params.row?.null_reason || cardData.null_reason || 'NR'} />
        : valueFormatted(params.value, cardData.var_type, varData.value),
    },
  ];

  return (
    error
      ? <ErrorLog errorInfo={error} component={'Comparison Card'} environment={env} />
      : (
        <Box sx={ComparisonStyles}>
          <div className={`comparison-card ${domainOptions ? 'dynamic-card' : 'static-card'}`}>
            <h2 className="comparison-card__title">{title}</h2>
            {
              (cardData?.group_average || cardData?.list?.length > 0) &&
              <Button onClick={() => {
                window._paq.push([
                  "trackEvent",
                  "Board InsightBoard",
                  `Clicked: ComparisonCard ${title} - order in groups`,
                ]);
                setShowRankingList(!showRankingList)
              }} className={`ranking-group-collapse`}>
                <KeyboardArrowDownIcon className={`comparison-card__toggle-icon ${showRankingList ? 'hide' : 'show'}`} sx={{ color: "black" }} />
                <span>Order in group: </span>
                <span className="value">{cardData.value !== null ? cardData.rank_in_group : <NullDisplay category={cardData.null_reason || "NR"} />}</span>
              </Button>
            }
            <div className="comparison-card__subtitle">
              {
                cardData && cardData.definition &&
                <span className="help-tooltip">
                  <TooltipCustom text={cardData.definition} />
                </span>
              }
              {
                domainOptions
                  ? <div className="comparison-card__form-select">
                    <Autocomplete
                      defaultValue={defaultDomainVar || domainOptions[0]}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      options={domainOptions}
                      fullWidth={true}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          setVarData(newValue);
                          storedCardInfo(newValue);
                          // Matomo code
                          window._paq?.push([
                            "trackEvent",
                            "Board InsightBoard",
                            `Clicked: ComparisonCard ${title}`,
                            `Selected: ${newValue.label}`,
                          ]);
                        }
                      }}
                      renderInput={(params) =>
                        <TextField {...params} label="" />
                      }
                    />
                  </div>
                  : cardData.kpi
              }
            </div>
            {
              cardData &&
              <div className="comparison-card__data">
                <div className={"comparison-card__average"}>
                  {
                    cardData.value === null
                      ? <div>Data unavailable for the selected year. This may indicate the district does not have {cardData.kpi}.</div>
                      : cardData.group_average === null
                        ? <div>Group average unavailable — comparison districts may have data from different years.</div>
                        : <div dangerouslySetInnerHTML={{ __html: sentenceRender() }} />
                  }
                </div>
                <div className="comparison-card__amount">
                  <div className="label">{referenceDistrict.label}</div>
                  <div className="value">{cardData.value === null ? <NullDisplay category={cardData.null_reason || "NR"} /> : cardData.value}</div>
                </div>
                <div className="comparison-card__group-amount">
                  <div className="label">{groupDefinition ?? groupName}</div>
                  <div className="value">{cardData.group_average === null ? <NullDisplay category="NR" /> : cardData.group_average}</div>
                </div>
                <Collapse in={showRankingList}>
                  <div style={{
                    height: (
                      (Object.keys(cardData.list).length < 50 ? Object.keys(cardData.list).length : 50) * 70 + 168), width: '100%'
                  }}
                    className={'comparison-card__ranking-table'}>
                    <DataGridPro
                      rows={[...cardData.list]
                        .sort((a, b) => {
                          // Sort NULL values to the bottom
                          if (a.value === null && b.value !== null) return 1;
                          if (a.value !== null && b.value === null) return -1;
                          // Keep original order for non-null values (already sorted by rown)
                          return 0;
                        })
                        .map((item, key) => {
                          let districtName = (year === 'MCD' && cardData.domain === "Teacher Salary" && item.year !== 2026) ? (item.district).replace(/\s*\(\d{4}\)\s*$/, '').trim() : item.district
                          return {
                            district: districtName,
                            rown: item.value === null ? null : item.rown,
                            value: item.value,
                            null_reason: item.null_reason,
                            id: key,
                            irn: item.irn,
                          }
                        })}
                      rowHeight={70}
                      columns={columns}
                      pageSize={50}
                      rowsPerPageOptions={[50]}
                      disableSelectionOnClick
                      components={{
                        Toolbar: CustomToolbar,
                      }}
                      disableColumnMenu
                      getRowClassName={
                        (params) => params.row.irn === referenceDistrict.value && 'row--highlighted'
                      }
                    />
                  </div>
                </Collapse>
                {
                  (cardData.group_average) &&
                  <div className="comparison-card__timeline ">
                    <Button onClick={() => setShowTimeline(!showTimeline)} >
                      {showTimeline ? 'Hide' : 'Show'} History
                      <img src={chartIcon} alt="line-chart-icon" className="chart-icon" />
                    </Button>
                    <Collapse in={showTimeline}>
                      <div className="timeline__chart">
                        {lineData &&
                          <LineChart data={lineData} />
                        }
                      </div>
                    </Collapse>
                  </div>
                }
              </div>
            }
          </div>
        </Box>
      )
  )
};

ComparisonCard.propTypes = {
  defaultDomainVar: PropTypes.oneOfType([
    PropTypes.func.isRequired,
    PropTypes.object.isRequired,
  ]),
  year: PropTypes.oneOfType([
    PropTypes.number.isRequired,
    PropTypes.string.isRequired,
  ]),
  title: PropTypes.string.isRequired,
  comparisonData: PropTypes.object,
  referenceDistrict: PropTypes.object.isRequired,
  irns: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  groupName: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  groupType: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
  ]),
  domainOptions: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  storedCardInfo: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired

}

export default ComparisonCard;
